'use client';

import React, { useCallback } from 'react';
import { NeighborhoodMapCore } from './NeighborhoodMapCore';
import { SelectedNeighborhoodChips } from './SelectedNeighborhoodChips';
import { BottomSheet, Button } from '@/components/ui';
import { useStore } from '@/providers/StoreProvider';
import { ExtendedNeighborhood } from './NeighborhoodGroupedList';
import { useAnalytics } from '@/hooks/useAnalytics';
import { cn } from '@/lib/cn';

export function NeighborhoodMapMobileContent({ onDone }: { onDone: () => void }) {
  const analytics = useAnalytics();
  const neighborhoods = useStore(s => s.neighborhoods.neighborhoods) as ExtendedNeighborhood[];
  const neighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const toggleNeighborhood = useStore(s => s.listingsFilters.toggleNeighborhood);
  const toggleNeighborhoodWithChildren = useStore(
    s => s.listingsFilters.toggleNeighborhoodWithChildren,
  );

  const handleNeighborhoodClick = useCallback(
    (neighborhoodId: string) => {
      const neighborhood = neighborhoods.find(n => n.id === neighborhoodId);
      if (!neighborhood) return;

      const hasChildren =
        Array.isArray(neighborhood.childrenIds) && neighborhood.childrenIds.length > 0;

      if (hasChildren) {
        toggleNeighborhoodWithChildren(neighborhoodId);
      } else {
        toggleNeighborhood(neighborhoodId);
      }
    },
    [neighborhoods, toggleNeighborhood, toggleNeighborhoodWithChildren],
  );

  return (
    <div className="!h-full min-h-0 flex flex-col">
      <div className="flex-1 relative min-h-0 overflow-hidden">
        <NeighborhoodMapCore
          neighborhoods={neighborhoods}
          selectedNeighborhoodIds={Array.isArray(neighborhoodIds) ? neighborhoodIds : []}
          onNeighborhoodClick={handleNeighborhoodClick}
          height="100%"
          width="100%"
        />
      </div>

      <div className={cn(neighborhoodIds.length > 0 ? 'py-4 pl-2 shrink-0 bg-primary' : '')}>
        <SelectedNeighborhoodChips mobile={true} />
      </div>
    </div>
  );
}

export function NeighborhoodMapMobileFooter({ onDone }: { onDone: () => void }) {
  const analytics = useAnalytics();
  const resetNeighborhoods = useStore(s => s.listingsFilters.resetNeighborhoods);
  const neighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const handleReset = useCallback(() => {
    analytics.track('listings_filter_allneighborhoods_map_resetselections');
    resetNeighborhoods();
  }, [analytics, resetNeighborhoods]);

  return (
    <div className="py-4 px-4 flex gap-4 justify-between items-center bg-primary">
      <Button
        disabled={neighborhoodIds.length === 0}
        onClick={handleReset}
        className="flex-1"
        variant="tertiary"
      >
        Reset
      </Button>
      <Button
        onClick={() => {
          analytics.track('listings_filter_allneighborhoods_map_seeresults');
          onDone();
        }}
        className="flex-1"
      >
        Done
      </Button>
    </div>
  );
}

export interface NeighborhoodMapMobileProps {
  open: boolean;
  onClose: () => void;
}

export function NeighborhoodMapMobile({ open, onClose }: NeighborhoodMapMobileProps) {
  const analytics = useAnalytics();

  return (
    <BottomSheet
      label="Select location"
      open={open}
      onClose={() => {
        analytics.track('listings_filter_allneighborhoods_map_cardclose');
        onClose();
      }}
      onBack={() => {
        analytics.track('listings_filter_allneighborhoods_map_goback');
        onClose();
      }}
      contentClassName="!h-dvh"
      bodyClassName="!px-0 !py-0"
      footer={<NeighborhoodMapMobileFooter onDone={onClose} />}
      footerClassName="!border-0"
    >
      <NeighborhoodMapMobileContent onDone={onClose} />
    </BottomSheet>
  );
}

export default NeighborhoodMapMobile;
